import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Film, Smartphone, UploadCloud } from "lucide-react";
import { useProfile } from "@/context/ProfileContext";
import { StudioImage } from "@/components/ui-nw/StudioImage";
import { SectionLabel } from "@/components/ui-nw/UserAvatar";
import { PageLoader } from "@/components/auth/Guards";
import { ShortsRow, VideoGrid } from "@/components/video/VideoCard";
import { VIDEO_CATEGORIES } from "@/components/video/videoConstants";
import { PAGES } from "@/constants/testIds";

const Banner = ({ isCreator }) => (
  <div className="relative overflow-hidden rounded-3xl border border-white/10 min-h-[240px] sm:min-h-[300px] flex items-end p-6 sm:p-10">
    <StudioImage name="video-set" eager alt="Music video production set under red lights" sizes="(min-width: 1152px) 1152px, 100vw" className="absolute inset-0 h-full w-full object-cover" />
    <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(5,5,8,0.2)_0%,rgba(5,5,8,0.85)_65%,#050508_100%)] sm:bg-[linear-gradient(90deg,#050508_5%,rgba(5,5,8,0.75)_45%,rgba(5,5,8,0.15))]" />
    <div className="relative space-y-3 max-w-lg">
      <SectionLabel>Videos</SectionLabel>
      <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight chrome-text">AI Video Theater</h1>
      <p className="text-sm sm:text-base text-slate-300">Music videos, visualizers and AI short films from the creators who made them.</p>
      {isCreator && <Link to="/upload?type=video" data-testid="videos-upload-cta" className="btn-red inline-flex items-center gap-2 px-5 py-2.5 text-sm"><UploadCloud className="h-4 w-4" /> Upload a video</Link>}
    </div>
  </div>
);

export default function VideosPage() {
  const { request, profile } = useProfile();
  const [category, setCategory] = useState("");
  const [shorts, setShorts] = useState(null);
  const [longs, setLongs] = useState(null);

  useEffect(() => { request("get", "/videos", null, { params: { format: "short" } }).then(setShorts).catch(() => setShorts([])); }, [request]);
  useEffect(() => {
    setLongs(null);
    request("get", "/videos", null, { params: { format: "long", ...(category ? { category } : {}) } }).then(setLongs).catch(() => setLongs([]));
  }, [category, request]);

  return (
    <div data-testid={PAGES.videos} className="space-y-10 animate-rise">
      <Banner isCreator={profile?.account_type === "creator"} />
      <section className="space-y-4">
        <div className="flex items-end justify-between">
          <h2 className="flex items-center gap-2 font-display text-2xl font-bold text-white"><Smartphone className="h-5 w-5 text-nw-red" /> Shorts</h2>
          {shorts?.length > 0 && <Link to="/shorts" data-testid="shorts-open-feed" className="text-sm font-semibold text-nw-red hover:underline">Watch feed</Link>}
        </div>
        {shorts === null ? <PageLoader /> : shorts.length === 0
          ? <p className="rounded-2xl border border-dashed border-white/15 p-6 text-sm text-slate-500">No shorts yet.</p>
          : <ShortsRow videos={shorts} />}
      </section>
      <section className="space-y-4">
        <h2 className="flex items-center gap-2 font-display text-2xl font-bold text-white"><Film className="h-5 w-5 text-nw-red" /> Theater</h2>
        <div className="flex gap-2 overflow-x-auto -mx-4 px-4 pb-1 scrollbar-none">
          {["", ...VIDEO_CATEGORIES].map((c) => (
            <button key={c || "all"} data-testid={`video-category-${(c || "all").toLowerCase().replace(/[^a-z0-9]/g, "")}`} onClick={() => setCategory(c)}
              className={`shrink-0 rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${category === c ? "bg-nw-red text-white" : "border border-white/15 text-slate-300 hover:border-white/30"}`}>
              {c || "All"}
            </button>
          ))}
        </div>
        {longs === null ? <PageLoader /> : <VideoGrid videos={longs} empty="No videos here yet." />}
      </section>
    </div>
  );
}
